import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { ArrowLeft, Briefcase, ChevronRight } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const AgentSignupPage = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [payoutPreference, setPayoutPreference] = useState('80_20');
  const [agentCountry, setAgentCountry] = useState('GB');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agentCountry || (agentCountry !== 'GB' && agentCountry !== 'US')) {
      toast({ variant: 'destructive', title: 'Country required', description: 'Please select United Kingdom or United States.' });
      return;
    }
    
    if (!fullName || fullName.trim().length < 2) {
      toast({ variant: 'destructive', title: 'Invalid Name', description: 'Please enter your full legal name' });
      return;
    }
    
    if (!agencyName || agencyName.trim().length < 2) {
      toast({ variant: 'destructive', title: 'Invalid Agency Name', description: 'Please enter your agency or business name' });
      return;
    }

    if (!acceptedTerms) {
      toast({ variant: 'destructive', title: 'Terms Required', description: 'Please accept the Terms and Conditions' });
      return;
    }
    
    setLoading(true);
    
    try {
      // Pass the agent-specific data
      const result = await signUp(email, password, {
          data: {
              fullName: fullName.trim(),
              artistName: agencyName.trim(), // Use artistName field in profile for agency name
              isAgent: true,
              payoutPreference: payoutPreference,
              country: agentCountry,
          }
      });
      
      if (result.error) {
        setLoading(false);
        // Toast is handled in AuthContext
      } else {
        // Success! AuthContext will handle redirect to /agent/dashboard
      }
    } catch (err) {
      console.error('Agent Signup error:', err);
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Agent Signup - Your Song Request</title>
        <meta name="description" content="Create your Agent account to onboard artists and manage your live revenue splits." />
      </Helmet>

      <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/10 via-gray-900/50 to-black"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-md"
        >
          <Link to="/agent" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6 transition-colors font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Agent Info
          </Link>

          <Card className="bg-gray-900/80 backdrop-blur-xl border border-blue-500/20 shadow-2xl">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <Briefcase className="w-8 h-8 text-blue-400" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-white tracking-tight">Agent Account</CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Manage your artists and earn a percentage of their live requests.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="agencyName" className="text-gray-200">Agency / Business Name</Label>
                  <Input
                    id="agencyName"
                    type="text"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    required
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20"
                    placeholder="e.g. Prestige Management"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-gray-200">Your Full Legal Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20"
                    placeholder="John Smith"
                  />
                  <p className="text-gray-500 text-[10px]">For payment processing and verification.</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200">Work Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20"
                    placeholder="you@agency.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-200">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-black/50 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20"
                    placeholder="••••••••"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agentCountry" className="text-gray-200">Country</Label>
                  <select
                    id="agentCountry"
                    value={agentCountry}
                    onChange={(e) => setAgentCountry(e.target.value === 'US' ? 'US' : 'GB')}
                    required
                    className="w-full h-10 rounded-md border border-gray-700 bg-black/50 px-3 text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
                  >
                    <option value="GB">United Kingdom</option>
                    <option value="US">United States</option>
                  </select>
                </div>

                <div className="pt-4 pb-2 border-t border-gray-800">
                  <Label className="text-gray-200 mb-3 block">Default Payout Split Preference</Label>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPayoutPreference('80_20')}
                      className={`p-3 rounded-xl border text-left flex flex-col transition-all ${
                        payoutPreference === '80_20' 
                        ? 'bg-blue-900/30 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)]' 
                        : 'bg-black/40 border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <span className={`text-sm font-bold ${payoutPreference === '80_20' ? 'text-blue-400' : 'text-gray-300'}`}>Agent Managed</span>
                      <span className="text-xs text-gray-500 mt-1">You receive 80%. You pay artists directly.</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setPayoutPreference('60_20_20')}
                      className={`p-3 rounded-xl border text-left flex flex-col transition-all ${
                        payoutPreference === '60_20_20' 
                        ? 'bg-purple-900/30 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.2)]' 
                        : 'bg-black/40 border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <span className={`text-sm font-bold ${payoutPreference === '60_20_20' ? 'text-purple-400' : 'text-gray-300'}`}>Auto Split</span>
                      <span className="text-xs text-gray-500 mt-1">You receive 20%. Artist receives 60% from YSR.</span>
                    </button>
                  </div>
                  <p className="text-gray-500 text-[10px] mt-2 italic">You can change this later in your dashboard.</p>
                </div>
                
                <div className="flex items-start space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 text-blue-600 bg-black/50 border-gray-700 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="terms" className="text-xs text-gray-400 leading-tight cursor-pointer">
                    I accept the{' '}
                    <Link to="/terms-of-service" target="_blank" className="text-blue-400 hover:text-blue-300 underline">Terms and Conditions</Link>
                    {' '}and{' '}
                    <Link to="/privacy-policy" target="_blank" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link>.
                  </label>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || !acceptedTerms}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-6 text-lg rounded-xl transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] disabled:opacity-50 disabled:cursor-not-allowed mt-4 flex items-center justify-center group"
                >
                  {loading ? 'Creating Account...' : (
                    <>Create Agent Account <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" /></>
                  )}
                </Button>
              </form>
              
              <div className="mt-8 text-center pt-6 border-t border-gray-800">
                <p className="text-gray-400 text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="text-blue-400 hover:text-blue-300 font-semibold">
                    Log in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default AgentSignupPage;
